import { useEffect, useRef, useState } from 'react'

interface SpacingByBreakpoint {
  mobile?: number // < 768px
  tablet?: number // >= 768px && < 1024px
  desktop?: number // >= 1024px
}

interface ColumnSpacingConfig {
  left: SpacingByBreakpoint
  right: SpacingByBreakpoint
}

interface BackgroundPatternConfig {
  leftImages: Array<{
    src: string
    height: number // altura em pixels
    className?: string
  }>
  rightImages: Array<{
    src: string
    height: number
    className?: string
  }>
  spacing: ColumnSpacingConfig
  minColumns: number // número mínimo de colunas
}

interface UseDynamicBackgroundPatternProps {
  contentRef: React.RefObject<HTMLElement>
  config: BackgroundPatternConfig
  totalLevels?: number // número total de níveis para calcular blocos
}

interface ColumnData {
  id: string
  offset: number
  cycleIndex?: number // índice do ciclo para alternar imagens da direita
}

/**
 * Hook para calcular dinamicamente quantas colunas de background pattern
 * são necessárias baseado na altura do conteúdo
 */
export function useDynamicBackgroundPattern({
  contentRef,
  config,
  totalLevels
}: UseDynamicBackgroundPatternProps) {
  const [leftColumns, setLeftColumns] = useState<ColumnData[]>([])
  const [rightColumns, setRightColumns] = useState<ColumnData[]>([])
  const [contentHeight, setContentHeight] = useState(0)

  // Usa refs para evitar loops infinitos
  const lastHeightRef = useRef(0)
  const lastLeftColumnsRef = useRef<ColumnData[]>([])
  const lastRightColumnsRef = useRef<ColumnData[]>([])
  const configRef = useRef(config)

  // Atualiza a ref do config quando mudar
  configRef.current = config

  useEffect(() => {
    if (!contentRef.current) return

    let rafId: number | null = null
    let timeoutId: NodeJS.Timeout | null = null

    // Calcula altura total de uma coluna (soma das alturas das imagens + espaçamentos)
    // Considera as alturas reais baseadas nas classes CSS responsivas
    const calculateColumnHeight = (images: typeof config.leftImages) => {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768

      return images.reduce((total, img) => {
        if (isMobile) {
          // Alturas reais no mobile baseadas nas classes CSS:
          // left-1: h-[500px] = 500px (agora é 500px no mobile também)
          // left-2: h-[250px] = 250px
          // right-1: h-[650px] = 650px
          // right-2: h-[650px] = 650px (sem mt-[100px] no mobile)
          if (img.src.includes('left-1')) {
            return total + 500 // h-[500px] no mobile
          }
          if (img.src.includes('left-2')) {
            return total + 250 // h-[250px] no mobile
          }
          if (img.src.includes('right-1')) {
            return total + 650 // h-[650px] no mobile
          }
          if (img.src.includes('right-2')) {
            return total + 650 // h-[650px] no mobile (sem margin-top)
          }
          return total + img.height
        }
        // Desktop: usa alturas configuradas
        // left-1: lg:h-[500px] = 500px
        // left-2: h-[250px] = 250px
        // right-1: h-[650px] = 650px
        // right-2: h-[650px] + mt-[100px] = 750px
        if (img.src.includes('left-1')) {
          return total + 500 // lg:h-[500px] no desktop
        }
        if (img.src.includes('left-2')) {
          return total + 250 // h-[250px] no desktop
        }
        if (img.src.includes('right-2')) {
          // right-2 tem mt-[100px], então altura efetiva é 650 + 100 = 750px
          return total + 750
        }
        return total + img.height
      }, 0)
    }

    const updateColumns = () => {
      // Cancela atualizações pendentes
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
        rafId = null
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }

      rafId = requestAnimationFrame(() => {
        rafId = null
        const element = contentRef.current
        if (!element) return

        // Obtém altura real do conteúdo (incluindo scroll)
        const height = Math.max(
          element.scrollHeight,
          element.offsetHeight,
          window.innerHeight
        )

        // Evita atualizações desnecessárias - só atualiza se a altura mudou significativamente
        if (Math.abs(lastHeightRef.current - height) < 50 && lastLeftColumnsRef.current.length > 0) {
          return
        }

        lastHeightRef.current = height
        setContentHeight(height)

        // Calcula quantas colunas são necessárias para cada lado
        // Adiciona 30% de margem para garantir cobertura durante scroll e animações
        const requiredHeight = height * 1.3

        // Usa a ref do config para evitar dependências de arrays
        const currentConfig = configRef.current

        // Recalcula altura considerando viewport atual (pode ter mudado de mobile para desktop)
        const currentLeftHeight = calculateColumnHeight(currentConfig.leftImages)
        const currentRightHeight = calculateColumnHeight(currentConfig.rightImages)

        const leftColumnsNeeded = Math.max(
          currentConfig.minColumns,
          Math.ceil(requiredHeight / currentLeftHeight)
        )
        const rightColumnsNeeded = Math.max(
          currentConfig.minColumns,
          Math.ceil(requiredHeight / currentRightHeight)
        )

        // Gera dados das colunas com offsets calculados
        const newLeftColumns: ColumnData[] = []
        const newRightColumns: ColumnData[] = []

        // Lógica baseada em blocos de 1920px (2 ciclos de 960px cada)
        // Cada nível tem 120px de altura
        // Um ciclo completo = 8 níveis × 120px = 960px
        // Um bloco completo = 2 ciclos = 16 níveis × 120px = 1920px
        const LEVELS_PER_BLOCK = 16 // 2 ciclos de 8 níveis cada
        const BLOCK_HEIGHT = 1920 // 16 níveis × 120px

        // Calcula quantos blocos são necessários baseado no número de níveis
        // Se tiver 18 níveis, precisa de 2 blocos (Math.ceil(18 / 16) = 2)
        const totalBlocks = totalLevels
          ? Math.ceil(totalLevels / LEVELS_PER_BLOCK)
          : Math.ceil(height / BLOCK_HEIGHT) // Fallback para altura se totalLevels não fornecido

        // Para a ESQUERDA: cada bloco tem 1920px
        // Duplica as 2 imagens: left-1, left-2, left-1, left-2
        // Sem espaçamento entre blocos, apenas empilhados
        for (let blockIndex = 0; blockIndex < totalBlocks; blockIndex++) {
          newLeftColumns.push({
            id: `left-${blockIndex}`,
            offset: blockIndex * BLOCK_HEIGHT // 0, 1920, 3840, 5760...
          })
        }

        // Para a DIREITA: cada bloco tem 1920px
        // Combina as 2 imagens: right-1, right-2
        // Sem espaçamento entre blocos, apenas empilhados
        for (let blockIndex = 0; blockIndex < totalBlocks; blockIndex++) {
          newRightColumns.push({
            id: `right-${blockIndex}`,
            offset: blockIndex * BLOCK_HEIGHT // 0, 1920, 3840, 5760...
          })
        }

        // Só atualiza se realmente mudou (comparação profunda)
        const leftChanged =
          lastLeftColumnsRef.current.length !== newLeftColumns.length ||
          lastLeftColumnsRef.current.some(
            (col, i) => col.id !== newLeftColumns[i]?.id || col.offset !== newLeftColumns[i]?.offset
          )

        const rightChanged =
          lastRightColumnsRef.current.length !== newRightColumns.length ||
          lastRightColumnsRef.current.some(
            (col, i) =>
              col.id !== newRightColumns[i]?.id ||
              col.offset !== newRightColumns[i]?.offset
          )

        if (leftChanged) {
          lastLeftColumnsRef.current = newLeftColumns
          setLeftColumns(newLeftColumns)
        }

        if (rightChanged) {
          lastRightColumnsRef.current = newRightColumns
          setRightColumns(newRightColumns)
        }
      })
    }

    // Atualiza imediatamente
    updateColumns()

    // Observa mudanças no tamanho do conteúdo com debounce
    const resizeObserver = new ResizeObserver(() => {
      timeoutId = setTimeout(() => {
        updateColumns()
      }, 100) // Debounce de 100ms
    })

    resizeObserver.observe(contentRef.current)

    // Observa mudanças no resize da janela com debounce
    const resizeHandler = () => {
      timeoutId = setTimeout(() => {
        updateColumns()
      }, 150) // Debounce maior para resize
    }

    window.addEventListener('resize', resizeHandler, { passive: true })

    // Atualiza inicialmente após um pequeno delay para garantir que o DOM está pronto
    timeoutId = setTimeout(() => {
      updateColumns()
    }, 50)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', resizeHandler)
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
    }
  }, [contentRef, totalLevels]) // Config é acessado via ref, então não precisa estar nas dependências

  return {
    leftColumns,
    rightColumns,
    contentHeight
  }
}
